import React, { useState, useEffect, useRef } from "react";
import OnboardingHeader from "./OnboardingHeader";
import StepProgressBar from "./StepProgressBar";
import OnboardingButton from "./OnboardingButton";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { updateStage } from "../../store/reducers/stageReducer";
import { useUpdateStagesMutation } from "../../services/api";
import { showNotification } from "../../services/showNotification";
import Tooltip from "../Tooltip";

interface VimeoTimeEvent {
  percent: number;
  duration: number;
  seconds: number;
}

interface VimeoPlayer {
  on(event: "timeupdate", callback: (data: VimeoTimeEvent) => void): void;
  on(event: "ended", callback: () => void): void;
  off(event: "timeupdate"): void;
  off(event: "ended"): void;
}

declare global {
  interface Window {
    Vimeo?: {
      Player: new (element: HTMLIFrameElement) => VimeoPlayer;
    };
  }
}

// This screen's step in the onboarding flow (see ONBOARDING_STEPS).
const STEP_KEY = "videoComplete" as const;

const StrategyVideo = () => {
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<VimeoPlayer | null>(null);

  const dispatch = useAppDispatch();
  const zohoCustomerId = useAppSelector((state) => state.login.zohoCustomerId);
  const [updateStages, { isLoading: isUpdating }] = useUpdateStagesMutation();

  const handleContinue = async () => {
    try {
      await updateStages({ zohoId: zohoCustomerId, key: STEP_KEY }).unwrap();
      dispatch(updateStage({ [STEP_KEY]: true }));
    } catch {
      showNotification("error", "Something went wrong. Please try again.");
    }
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://player.vimeo.com/api/player.js";
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (iframeRef.current && window.Vimeo) {
        const player = new window.Vimeo.Player(iframeRef.current);
        playerRef.current = player;

        player.on("timeupdate", (data: VimeoTimeEvent) => {
          if (data.percent >= 0.95) {
            setIsCompleted(true);
          }
        });

        player.on("ended", () => {
          setIsCompleted(true);
        });
      }
    };

    return () => {
      if (playerRef.current) {
        playerRef.current.off("timeupdate");
        playerRef.current.off("ended");
      }
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <OnboardingHeader />

      <main className="w-full max-w-5xl mx-auto p-10">
        <StepProgressBar stepKey={STEP_KEY} />

        <div className="mt-6 flex flex-col items-center">
          <h1 className="font-['Canela_Text',_serif] font-bold text-3xl uppercase text-[#000000] text-center">
            See how our strategy Works
          </h1>

          <div className="mt-6 w-full max-w-5xl">
            <div className="w-full relative aspect-video bg-black border-[1.27px] border-[#A2B2C833] overflow-hidden shadow-lg">
              <iframe 
                ref={iframeRef}
                src="https://player.vimeo.com/video/949543563?h=1cc197bf6a&badge=0&autopause=0&player_id=0&app_id=58479" 
                className="w-full h-full absolute top-0 left-0" 
                frameBorder="0" 
                allow="autoplay; fullscreen; picture-in-picture" 
                allowFullScreen 
                title="Animated Overview"
              ></iframe>
            </div>
          </div>

          <div className="mt-6 w-full max-w-[400px]">
            <Tooltip 
              text="Please completely watch the video before continuing"
              show={!isCompleted}
              className="w-full"
            >
              <OnboardingButton
                title="CONTINUE"
                variant="filled"
                onClick={handleContinue}
                loading={isUpdating}
                disabled={!isCompleted || isUpdating}
              />
            </Tooltip>
          </div>
        </div>
      </main>
    </div>
  );
};

export default StrategyVideo;